import { useState, useEffect } from 'react';
import { useStockFeed } from './useStockFeed';
import './ReEntryAnalyzer.css';

/* ═══════════════════════════════════════════
   BD Collins ATH-based DCA tiers
   ═══════════════════════════════════════════ */
const BD_TIERS = [
  { label: 'Within 15% of ATH',  min: 0,  max: 15,      deployPct: 20, color: 'low',    desc: 'Conservative — market near highs' },
  { label: '15–25% below ATH',   min: 15, max: 25,       deployPct: 50, color: 'medium', desc: 'Moderate — meaningful pullback'    },
  { label: '25–35% below ATH',   min: 25, max: 35,       deployPct: 70, color: 'high',   desc: 'Aggressive — significant correction' },
  { label: '35%+ below ATH',     min: 35, max: Infinity,  deployPct: 90, color: 'max',    desc: 'Maximum — deep correction / bear market' },
];

function getBdTier(distPct) {
  return BD_TIERS.find(t => distPct >= t.min && distPct < t.max) ?? BD_TIERS[0];
}

/* ═══════════════════════════════════════════
   VIX penalty for 3x LETFs
   ═══════════════════════════════════════════ */
function getVixInfo(vix) {
  if (!vix || vix <= 0) return { level: 'unknown', penaltyPct: 0, color: 'neutral', label: '—' };
  if (vix < 20) return { level: 'low',      penaltyPct: 0,  color: 'green',  label: 'Low',      desc: 'No penalty — stable market'          };
  if (vix < 25) return { level: 'elevated', penaltyPct: 25, color: 'amber',  label: 'Elevated', desc: '−25% for 3x ETFs — elevated risk'    };
  if (vix < 30) return { level: 'high',     penaltyPct: 50, color: 'red',    label: 'High',     desc: '−50% for 3x ETFs — high volatility'  };
  return              { level: 'extreme',   penaltyPct: 75, color: 'red',    label: 'Extreme',  desc: '−75% for 3x ETFs — stand aside'      };
}

/* ═══════════════════════════════════════════
   Macro checklist items
   ═══════════════════════════════════════════ */
const MACRO_ITEMS = [
  { id: 'fed',       label: 'Fed policy stable',                        hint: 'No surprise hikes or aggressive tightening signals',   autoId: null   },
  { id: 'vix',       label: 'VIX below 25',                             hint: 'Auto-detected from live VIX feed above',               autoId: 'vix'  },
  { id: 'stable',    label: 'Market stabilizing',                       hint: 'Not in free-fall — showing signs of support / bounce', autoId: null   },
  { id: 'berkshire', label: 'Berkshire not aggressively building cash',  hint: "Buffett's cash pile not surging (BD Collins signal)",  autoId: null   },
  { id: 'shock',     label: 'No major macro shock unfolding',            hint: 'No active recession, crisis, or geopolitical event',   autoId: null   },
];

/* ═══════════════════════════════════════════
   localStorage keys
   ═══════════════════════════════════════════ */
const ANNUAL_KEY = 'isa_annual_target';
const ATH_KEY    = 'isa_qqq_ath';

/* ═══════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════ */
function fmt(n)   { return Math.round(n).toLocaleString('en-US'); }
function fmtE(n)  { return '€' + fmt(n); }

/* ─────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────── */
export default function ReEntryAnalyzer() {

  /* ── Mode: regular DCA vs post stop-out redeployment ── */
  const [mode, setMode] = useState('dca'); // 'dca' | 'reentry'

  /* ── Annual target & monthly suggestion ── */
  const [annualTarget, setAnnualTarget] = useState(() => {
    try { return Number(localStorage.getItem(ANNUAL_KEY)) || 20000; } catch { return 20000; }
  });

  /* ── Capital input for post stop-out mode ── */
  const [capitalInput, setCapitalInput] = useState('');

  /* ── QQQ ATH ── */
  const [qqqAth, setQqqAth] = useState(() => {
    try { return localStorage.getItem(ATH_KEY) || ''; } catch { return ''; }
  });

  /* ── Macro checklist ── */
  const [macroChecked, setMacroChecked] = useState({
    fed: false, vix: false, stable: false, berkshire: false, shock: false,
  });

  /* ── Live data: QQQ price + VIX ── */
  const { getStockData } = useStockFeed(['QQQ', '^VIX']);
  const qqqData   = getStockData('QQQ');
  const vixData   = getStockData('^VIX');
  const qqqPrice  = qqqData.price  ?? null;
  const vixLevel  = vixData.price  ?? null;
  const qqq52wHigh = qqqData.ath   ?? null;

  /* ── Auto-tick VIX macro checkbox when live VIX updates ── */
  useEffect(() => {
    if (vixLevel !== null) {
      setMacroChecked(prev => ({ ...prev, vix: vixLevel < 25 }));
    }
  }, [vixLevel]);

  /* ── Persist settings ── */
  function handleAnnualChange(val) {
    const n = Number(val) || 0;
    setAnnualTarget(n);
    try { localStorage.setItem(ANNUAL_KEY, String(n)); } catch {}
  }

  function handleAthChange(val) {
    setQqqAth(val);
    try { localStorage.setItem(ATH_KEY, val); } catch {}
  }

  function toggleMacro(id) {
    if (id === 'vix') return; // auto-controlled
    setMacroChecked(prev => ({ ...prev, [id]: !prev[id] }));
  }

  /* ═══════════════════════════════════════════
     Calculations
     ═══════════════════════════════════════════ */
  const monthlySuggested = Math.round(annualTarget / 12);

  /* Capital to work with */
  const capital = mode === 'reentry'
    ? (Number(capitalInput) || 0)
    : monthlySuggested;

  /* Pillar 1: QQQ ATH distance */
  const athValue = Number(qqqAth) || 0;
  const qqqDist  = (qqqPrice && athValue > 0)
    ? Math.max(0, ((athValue - qqqPrice) / athValue) * 100)
    : null;
  const bdTier = qqqDist !== null ? getBdTier(qqqDist) : null;

  /* Pillar 2: VIX */
  const vixInfo = getVixInfo(vixLevel);

  /* Pillar 3: Macro score */
  const macroScore   = Object.values(macroChecked).filter(Boolean).length;
  const macroPenalty = macroScore < 3 ? 25 : 0;

  /* Pillar 4: Final recommendation */
  const baseAmount    = bdTier   != null ? (capital * bdTier.deployPct / 100)         : null;
  const afterVix      = baseAmount != null ? baseAmount * (1 - vixInfo.penaltyPct / 100) : null;
  const afterMacro    = afterVix   != null ? afterVix   * (1 - macroPenalty / 100)       : null;

  /* In reentry mode: enforce 80% max (keep 20% cash reserve per framework rule) */
  const deployAmount  = (mode === 'reentry' && afterMacro != null && capital > 0)
    ? Math.min(afterMacro, capital * 0.80)
    : afterMacro;

  const tqqqAmount = deployAmount != null ? deployAmount * 0.60 : null;
  const uproAmount = deployAmount != null ? deployAmount * 0.40 : null;
  const cashKeep   = (mode === 'reentry' && capital > 0 && deployAmount != null)
    ? capital - deployAmount
    : null;

  /* Overall signal */
  const signalLevel =
    !bdTier                              ? 'neutral' :
    macroScore >= 4 && vixInfo.penaltyPct === 0 ? 'green'  :
    macroScore >= 3 && vixInfo.penaltyPct <= 25 ? 'amber'  : 'red';

  /* ═══════════════════════════════════════════
     Render
     ═══════════════════════════════════════════ */
  return (
    <div className="ra-screen">

      {/* ── Page header ── */}
      <div className="ra-header-card">
        <h2 className="ra-page-title">🎯 Re-Entry Analyzer</h2>
        <p className="ra-page-sub">BD Collins framework · Live QQQ + VIX · Macro checklist</p>

        {/* Mode toggle */}
        <div className="ra-mode-toggle">
          <button
            className={`ra-mode-btn${mode === 'dca' ? ' ra-mode-btn--active' : ''}`}
            onClick={() => setMode('dca')}
          >
            📅 Regular DCA
          </button>
          <button
            className={`ra-mode-btn${mode === 'reentry' ? ' ra-mode-btn--active' : ''}`}
            onClick={() => setMode('reentry')}
          >
            🔄 Post Stop-Out
          </button>
        </div>

        {/* Post stop-out reminder banner */}
        {mode === 'reentry' && (
          <div className="ra-reentry-banner">
            <span className="ra-banner-icon">⚖️</span>
            <span>Framework rule: <strong>80% of proceeds → TQQQ/UPRO</strong> per BD Collins · <strong>20% → cash reserve</strong></span>
          </div>
        )}
      </div>

      {/* ── Capital section ── */}
      <div className="ra-card">
        <div className="ra-card-title">💰 Capital</div>

        <div className="ra-field-row">
          <label className="ra-field-label">Annual Target (€)</label>
          <div className="ra-input-group">
            <span className="ra-currency">€</span>
            <input
              type="number"
              className="form-input ra-input"
              value={annualTarget || ''}
              onChange={e => handleAnnualChange(e.target.value)}
              placeholder="20000"
            />
          </div>
          <span className="ra-field-hint">→ <strong>€{monthlySuggested.toLocaleString()}</strong> / month suggested</span>
        </div>

        {mode === 'reentry' && (
          <div className="ra-field-row">
            <label className="ra-field-label">Proceeds from Stop-Out (€)</label>
            <div className="ra-input-group">
              <span className="ra-currency">€</span>
              <input
                type="number"
                className="form-input ra-input"
                value={capitalInput}
                onChange={e => setCapitalInput(e.target.value)}
                placeholder="0"
              />
            </div>
            <span className="ra-field-hint">capital available to redeploy</span>
          </div>
        )}

        {mode === 'dca' && (
          <div className="ra-capital-display">
            <span className="ra-capital-label">This month's suggested budget</span>
            <span className="ra-capital-amount">€{monthlySuggested.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════
          PILLAR 1 — QQQ ATH Distance
          ══════════════════════════════════════════ */}
      <div className="ra-pillar-card">
        <div className="ra-pillar-header">
          <div className="ra-pillar-badge">1</div>
          <span className="ra-pillar-title">QQQ ATH Distance</span>
          <span className={`live-badge live-badge--${qqqData.status}`}>
            <span className="live-dot" />
            {qqqData.status === 'live' ? 'Live' : qqqData.status === 'offline' ? 'Offline' : '…'}
          </span>
        </div>

        <div className="ra-pillar-body">
          {/* Live QQQ price display */}
          <div className="ra-data-strip">
            <div className="ra-data-item">
              <span className="ra-data-label">QQQ Price</span>
              <span className="ra-data-value">
                {qqqPrice ? `$${qqqPrice.toFixed(2)}` : '—'}
              </span>
            </div>
            {qqqDist !== null && (
              <div className="ra-data-item">
                <span className="ra-data-label">Below ATH</span>
                <span className={`ra-data-value ra-dist--${bdTier?.color}`}>
                  {qqqDist.toFixed(1)}%
                </span>
              </div>
            )}
          </div>

          {/* ATH input */}
          <div className="ra-field-row">
            <label className="ra-field-label">QQQ All-Time High ($)</label>
            <div className="ra-input-group">
              <span className="ra-currency">$</span>
              <input
                type="number"
                className="form-input ra-input"
                value={qqqAth}
                onChange={e => handleAthChange(e.target.value)}
                placeholder="0.00"
              />
            </div>
            {qqq52wHigh && (
              <button
                className="ra-use-btn"
                onClick={() => handleAthChange(qqq52wHigh.toFixed(2))}
              >
                ↙ use 52W high: ${qqq52wHigh.toFixed(2)}
              </button>
            )}
          </div>

          {/* BD Collins tier strip */}
          <div className="ra-tiers">
            {BD_TIERS.map(tier => {
              const isActive = bdTier === tier;
              return (
                <div key={tier.label} className={`ra-tier-row${isActive ? ' ra-tier-row--active' : ''}`}>
                  <span className="ra-tier-label">{tier.label}</span>
                  <div className="ra-tier-right">
                    <span className="ra-tier-desc">{tier.desc}</span>
                    <span className={`ra-tier-pct ra-tier-pct--${tier.color}`}>{tier.deployPct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          PILLAR 2 — VIX Volatility Level
          ══════════════════════════════════════════ */}
      <div className="ra-pillar-card">
        <div className="ra-pillar-header">
          <div className="ra-pillar-badge">2</div>
          <span className="ra-pillar-title">VIX — Volatility</span>
          <span className={`live-badge live-badge--${vixData.status}`}>
            <span className="live-dot" />
            {vixData.status === 'live' ? 'Live' : vixData.status === 'offline' ? 'Offline' : '…'}
          </span>
        </div>

        <div className="ra-pillar-body">
          <div className="ra-vix-row">
            <span className={`ra-vix-number ra-vix--${vixInfo.color}`}>
              {vixLevel ? vixLevel.toFixed(1) : '—'}
            </span>
            <div className="ra-vix-info">
              <span className={`ra-vix-level ra-vix--${vixInfo.color}`}>{vixInfo.label}</span>
              {vixInfo.desc && <span className="ra-vix-desc">{vixInfo.desc}</span>}
            </div>
          </div>

          {/* VIX scale */}
          <div className="ra-vix-scale">
            {[
              { label: '< 20',   color: 'green',  tag: 'Low',      penalty: 'No penalty' },
              { label: '20–25',  color: 'amber',  tag: 'Elevated', penalty: '−25%'       },
              { label: '25–30',  color: 'red',    tag: 'High',     penalty: '−50%'       },
              { label: '30+',    color: 'red',    tag: 'Extreme',  penalty: '−75%'       },
            ].map(v => (
              <div
                key={v.label}
                className={`ra-vix-scale-item${vixInfo.label === v.tag ? ' active' : ''}`}
              >
                <span className={`ra-vix-scale-label ra-vix--${v.color}`}>{v.label}</span>
                <span className="ra-vix-scale-penalty">{v.penalty}</span>
              </div>
            ))}
          </div>

          {vixLevel && vixLevel >= 25 && (
            <div className="ra-vix-warning">
              ⚠ VIX ≥ 25 — one of your macro pre-conditions is not met. Check macro checklist below.
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════
          PILLAR 3 — Macro Checklist
          ══════════════════════════════════════════ */}
      <div className="ra-pillar-card">
        <div className="ra-pillar-header">
          <div className="ra-pillar-badge">3</div>
          <span className="ra-pillar-title">Macro Checklist</span>
          <span className={`ra-score-badge ra-score--${macroScore >= 4 ? 'green' : macroScore >= 3 ? 'amber' : 'red'}`}>
            {macroScore}/5
          </span>
        </div>

        <div className="ra-pillar-body">
          {MACRO_ITEMS.map(item => (
            <label
              key={item.id}
              className={`ra-macro-row${macroChecked[item.id] ? ' ra-macro-row--checked' : ''}${item.autoId ? ' ra-macro-row--auto' : ''}`}
              onClick={item.id === 'vix' ? undefined : undefined}
            >
              <input
                type="checkbox"
                className="ra-macro-check"
                checked={macroChecked[item.id]}
                onChange={() => toggleMacro(item.id)}
                disabled={item.id === 'vix'}
              />
              <div className="ra-macro-content">
                <span className="ra-macro-label">{item.label}</span>
                <span className="ra-macro-hint">{item.hint}</span>
              </div>
              {item.id === 'vix' && (
                <span className="ra-macro-auto-badge">auto</span>
              )}
            </label>
          ))}

          {/* Macro score summary */}
          <div className={`ra-macro-summary ra-macro-summary--${macroScore >= 4 ? 'green' : macroScore >= 3 ? 'amber' : 'red'}`}>
            {macroScore >= 4
              ? `✓ ${macroScore}/5 conditions met — macro environment favorable`
              : macroScore === 3
              ? `⚠ ${macroScore}/5 conditions met — proceed cautiously`
              : `⚠ Only ${macroScore}/5 conditions met — additional −25% caution applied`}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          PILLAR 4 — Deployment Recommendation
          ══════════════════════════════════════════ */}
      <div className={`ra-pillar-card ra-pillar-card--result ra-result--${signalLevel}`}>
        <div className="ra-pillar-header">
          <div className="ra-pillar-badge ra-pillar-badge--result">4</div>
          <span className="ra-pillar-title">Deployment Recommendation</span>
        </div>

        <div className="ra-pillar-body">
          {!bdTier ? (
            <div className="ra-result-empty">
              Enter QQQ ATH above to generate a recommendation
            </div>
          ) : capital === 0 && mode === 'reentry' ? (
            <div className="ra-result-empty">
              Enter your available capital above to generate a recommendation
            </div>
          ) : (
            <>
              {/* Calculation breakdown */}
              <div className="ra-calc">
                <div className="ra-calc-row">
                  <span className="ra-calc-label">Available capital</span>
                  <span className="ra-calc-value">{fmtE(capital)}</span>
                </div>
                <div className="ra-calc-row ra-calc-row--tier">
                  <span className="ra-calc-label">
                    BD Collins — {bdTier.label} ({bdTier.deployPct}% deploy)
                  </span>
                  <span className="ra-calc-value">{fmtE(baseAmount ?? 0)}</span>
                </div>
                {vixInfo.penaltyPct > 0 && (
                  <div className="ra-calc-row ra-calc-row--deduct">
                    <span className="ra-calc-label">VIX {vixInfo.label} penalty (−{vixInfo.penaltyPct}%)</span>
                    <span className="ra-calc-value ra-calc-deduct">
                      −{fmtE((baseAmount ?? 0) * vixInfo.penaltyPct / 100)}
                    </span>
                  </div>
                )}
                {macroPenalty > 0 && (
                  <div className="ra-calc-row ra-calc-row--deduct">
                    <span className="ra-calc-label">Macro caution &lt;3/5 (−{macroPenalty}%)</span>
                    <span className="ra-calc-value ra-calc-deduct">
                      −{fmtE((afterVix ?? 0) * macroPenalty / 100)}
                    </span>
                  </div>
                )}
                {mode === 'reentry' && deployAmount !== afterMacro && (
                  <div className="ra-calc-row ra-calc-row--deduct">
                    <span className="ra-calc-label">80% max (keep 20% as cash reserve)</span>
                    <span className="ra-calc-value ra-calc-note">capped</span>
                  </div>
                )}
                <div className="ra-calc-divider" />
                <div className="ra-calc-row ra-calc-row--total">
                  <span className="ra-calc-label">Total to deploy</span>
                  <span className="ra-calc-value ra-calc-total">{fmtE(deployAmount ?? 0)}</span>
                </div>
              </div>

              {/* 60/40 TQQQ/UPRO split */}
              <div className="ra-split">
                <div className="ra-split-header">60/40 TQQQ / UPRO split</div>
                <div className="ra-split-items">
                  <div className="ra-split-item ra-split-item--tqqq">
                    <div className="ra-split-top">
                      <span className="ra-split-ticker">TQQQ</span>
                      <span className="ra-split-ratio">3× Nasdaq · 60%</span>
                    </div>
                    <span className="ra-split-amount">{fmtE(tqqqAmount ?? 0)}</span>
                  </div>
                  <div className="ra-split-item ra-split-item--upro">
                    <div className="ra-split-top">
                      <span className="ra-split-ticker">UPRO</span>
                      <span className="ra-split-ratio">3× S&amp;P 500 · 40%</span>
                    </div>
                    <span className="ra-split-amount">{fmtE(uproAmount ?? 0)}</span>
                  </div>
                  {mode === 'reentry' && cashKeep !== null && cashKeep > 0 && (
                    <div className="ra-split-item ra-split-item--cash">
                      <div className="ra-split-top">
                        <span className="ra-split-ticker">CASH</span>
                        <span className="ra-split-ratio">reserve · keep</span>
                      </div>
                      <span className="ra-split-amount">{fmtE(cashKeep)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* €100K threshold note */}
              <p className="ra-split-note">
                💡 Switch to 50/50 split when portfolio reaches €100K to reduce concentration risk
              </p>

              {/* Final signal */}
              <div className={`ra-signal ra-signal--${signalLevel}`}>
                {signalLevel === 'green' && '✓ Green light — all conditions favorable for deployment'}
                {signalLevel === 'amber' && '⚠ Amber — deploy cautiously, size down if in doubt'}
                {signalLevel === 'red'   && '🔴 Caution — multiple risk factors active, consider waiting'}
              </div>

              {/* Market timing reminder */}
              {vixLevel && vixLevel >= 25 && (
                <div className="ra-timing-note">
                  📅 US market closes at 4:00am Shanghai time — set IBKR orders before then
                </div>
              )}
            </>
          )}
        </div>
      </div>

    </div>
  );
}
